import { ScopeChangeRequest } from '@/features/admin/scope-change-requests/ScopeChangeRequest'
import { type ScopeChangeRequestStore } from '@/features/admin/scope-change-requests/ScopeChangeRequestStore'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import type { AlertErrorHandlerFactory } from '@/features/shell'

/**
 * How an admin approves a change request that needs client approval.
 */
export type ScopeChangeRequestActionType =
  'ApproveAsAdmin' | 'ApproveOnBehalfOfClient'

export class ScopeChangeRequestActionSelectionDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable accessor scopeChangeRequest = ScopeChangeRequest.none

  @observable
  accessor actionType: ScopeChangeRequestActionType = 'ApproveAsAdmin'

  constructor(
    private readonly _scopeChangeRequestStore: ScopeChangeRequestStore,
    private readonly _refreshStatusFilter: () => Promise<void>,
    private readonly _onSave: () => Promise<void>,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  @action.bound
  setAction(actionType: ScopeChangeRequestActionType) {
    this.actionType = actionType
  }

  @action.bound
  open(scopeChangeRequest: ScopeChangeRequest) {
    this.scopeChangeRequest = scopeChangeRequest

    this.actionType =
      scopeChangeRequest.price > 0
        ? 'ApproveAsAdmin'
        : 'ApproveOnBehalfOfClient'

    this.modal.show()
  }

  submit = task.resolved(async () => {
    const skipClientApproval = this.actionType === 'ApproveOnBehalfOfClient'

    await this._scopeChangeRequestStore
      .approveScopeChangeRequest(this.scopeChangeRequest, skipClientApproval)
      .catch(
        this._createAlertErrorHandler('Approving the change request failed'),
      )

    if (skipClientApproval) {
      await this._refreshStatusFilter()
    }

    void this._onSave()
    this.modal.close()
  })
}
